import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readStorage, writeStorage } from '@/shared/lib/storage';
import { haptics } from '@/shared/lib/telegram';
import { ErrorBlock, Skeleton } from '@/shared/ui';
import { flameTier } from '../model/streakTier';
import { useStreak } from '../model/useStreak';
import { Flame } from './Flame';
import { StreakSheet } from './StreakSheet';

const RING_SIZE = 44;
const RING_STROKE = 7;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
/**
 * Фиолетовый Periwinkle — тот же тон, что у выполненных дней в календаре
 * стрика (`StreakSheet`), из палитры ролевых игр. На ступень насыщеннее
 * карточного #C7D2FE: тот по светлоте почти равен серой дорожке кольца и в
 * тонкой линии сливается с ней.
 */
const RING_FILL = '#A5B4FC';

/**
 * Как показывать прогресс: «3 из 10 мин» или «03:12 / 10:00». Тап по цели
 * переключает, выбор помнится на устройстве — это удобство одного
 * человека, серверу о нём знать незачем.
 */
type ProgressFormat = 'minutes' | 'clock';
const FORMAT_KEY = 'streak:progress-format';
const reviveFormat = (raw: unknown): ProgressFormat | null =>
  raw === 'minutes' || raw === 'clock' ? raw : null;

function clock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

/**
 * Полоса вверху главной: кольцо и «N из M мин» дневной цели слева, огонёк со
 * стриком справа — тап по огоньку открывает календарь стрика. По референсу
 * Дмитрия, без флага языка: он и так в шапке.
 */
export function DailyGoalBar() {
  const { t } = useTranslation('DailyGoalBar');
  const { streak, error, reload } = useStreak();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [format, setFormat] = useState<ProgressFormat>(
    () => readStorage(FORMAT_KEY, reviveFormat) ?? 'minutes',
  );

  const toggleFormat = () => {
    const next = format === 'minutes' ? 'clock' : 'minutes';
    haptics.select();
    setFormat(next);
    writeStorage(FORMAT_KEY, next);
  };

  // Без этой ветки упавший запрос оставлял шиммер навсегда: `streak` так и null.
  if (error != null) return <ErrorBlock onRetry={reload} />;

  if (!streak) {
    return (
      <div className="flex h-11 items-center gap-3" aria-hidden="true">
        <Skeleton className="size-11 rounded-full" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-8 w-14 rounded-full" />
      </div>
    );
  }

  const goalSeconds = streak.goalMinutes * 60;
  const progress = Math.min(streak.todaySeconds / goalSeconds, 1);
  const minutes = Math.floor(streak.todaySeconds / 60);
  const tier = flameTier(streak.streak, streak.todaySeconds, streak.goalMinutes);
  const progressLabel = t('progress', { minutes, goal: streak.goalMinutes });

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={toggleFormat}
        aria-label={progressLabel}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          className="shrink-0 -rotate-90"
          aria-hidden="true"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            className="stroke-glass-3"
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_LENGTH}
            strokeDashoffset={RING_LENGTH * (1 - progress)}
            stroke={RING_FILL}
            className="transition-[stroke-dashoffset] duration-700 ease-out"
            opacity={progress > 0 ? 1 : 0}
          />
        </svg>

        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[11px] font-semibold tracking-wide text-ink-muted uppercase">
            {t('title')}
          </span>
          <span className="text-lg leading-tight font-extrabold text-ink tabular-nums">
            {format === 'clock'
              ? `${clock(streak.todaySeconds)} / ${clock(goalSeconds)}`
              : progressLabel}
          </span>
        </div>
      </button>

      <button
        type="button"
        onClick={() => {
          haptics.select();
          setSheetOpen(true);
        }}
        aria-label={t('streakButton', { count: streak.streak })}
        className="flex shrink-0 items-center gap-1 rounded-full py-1 pr-1 pl-2 active:bg-glass-2"
      >
        <Flame tier={tier} className="h-8 w-7" />
        <span
          className={`text-xl font-extrabold tabular-nums ${tier > 0 ? 'text-ink' : 'text-ink-subtle'}`}
        >
          {streak.streak}
        </span>
      </button>

      {sheetOpen && <StreakSheet streak={streak} onClose={() => setSheetOpen(false)} />}
    </div>
  );
}
